import React, { useState, useEffect } from 'react';
import { X, Send } from 'lucide-react';

interface PromptModalProps {
  isOpen: boolean;
  title: string;
  description: string;
  initialValue?: string;
  onClose: () => void;
  onSubmit: (value: string) => void;
}

const PromptModal: React.FC<PromptModalProps> = ({ isOpen, title, description, initialValue = '', onClose, onSubmit }) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    if (isOpen) {
      setValue(initialValue);
    }
  }, [isOpen, initialValue]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim()) {
      onSubmit(value.trim());
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4 animate-in fade-in duration-300">
      <div className="bg-white rounded-[2rem] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 border border-slate-100">
        <div className="flex justify-between items-center p-6 border-b border-slate-50">
          <h3 className="text-lg font-black text-slate-900 italic tracking-tight">{title}</h3>
          <button onClick={onClose} title="Închide" aria-label="Închide fereastra" className="w-8 h-8 flex items-center justify-center bg-slate-50 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <p className="text-sm font-medium text-slate-500 leading-relaxed">
            {description}
          </p>

          <div>
            <textarea
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Ex: Părțile sunt X și Y, obiectul este un apartament..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all min-h-[120px] resize-none"
            />
          </div>

          <div className="flex gap-3">
            <button 
              type="button" 
              onClick={onClose}
              className="flex-1 py-3 text-slate-400 font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 rounded-xl transition-all"
            >
              Anulează
            </button>
            <button 
              type="submit"
              disabled={!value.trim()}
              className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-200 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Send className="w-3.5 h-3.5" />
              Generează Draft
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PromptModal;